import { useCallback, useEffect, useState } from 'react';
import { analyseLine, suggestRejector, type Finding, type Station } from '@visionqc/shared';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';

/**
 * The line: what equipment is installed and how it is addressed.
 *
 * Ordered by position along the belt rather than by name, because that is the
 * order a part meets them in and the order reject timing depends on. A device
 * whose profile has never been verified against real hardware is marked on
 * every row — it is running on somebody's best guess at an address, and that
 * is not a detail to bury in a settings page.
 */

export interface DeviceProfile {
  id: number;
  brand: string;
  model: string;
  role: string;
  driver: string;
  description: string | null;
  defaultSettings: Record<string, unknown>;
  required: string[];
  actuationMs: number | null;
  dwellMs: number | null;
  verified: boolean;
  verifiedBy: string | null;
  notes: string | null;
  listPrice: number | null;
  installHours: number | null;
  sku: string | null;
  vendor: string | null;
  vendorRef: string | null;
  vendorUrl: string | null;
  invoiceProductId: number | null;
  catalogueSyncedAt: string | null;
}

export interface LineDevice {
  id: number;
  stationId: number | null;
  station: string | null;
  profileId: number | null;
  brand: string | null;
  model: string | null;
  profileVerified: boolean | null;
  name: string;
  role: string;
  driver: string;
  positionMm: number | null;
  sequence: number;
  settings: Record<string, unknown>;
  actuationMs: number | null;
  dwellMs: number | null;
  actsOn: string[];
  enabled: boolean;
  notes: string | null;
}

const ROLE_ICON: Record<string, string> = {
  rejector: '⇥',
  collector: '▣',
  feeder: '⇤',
  trigger: '◉',
  safety: '⛨',
  mechanical: '⚙',
  conveyor: '⇉',
  encoder: '⟳',
  light: '☀',
  camera: '⊙',
  io: '⊞',
  plc: '▤',
};

/**
 * A colour per role.
 *
 * Drawn from the accent hues, never the status set: good/warning/bad already
 * mean "is this device ready to run" on the left border of every card. Reusing
 * red for a role would put two meanings on one colour, on a page where one of
 * them is about whether an actuator is safe to fire.
 */
const ROLE_COLOUR: Record<string, string> = {
  rejector: '#a855f7',
  collector: '#f472b6',
  feeder: '#38bdf8',
  trigger: '#3b82f6',
  // Safety gets the reserved warning colour deliberately: it is the one row on
  // this page that is not under software control, and that should read as
  // different at a glance.
  safety: '#fab219',
  mechanical: '#94a3b8',
  conveyor: '#14b8a6',
  encoder: '#f59e0b',
  light: '#ec4899',
  camera: '#22c55e',
  io: '#64748b',
  plc: '#64748b',
};

const ROLE_LABEL: Record<string, string> = {
  rejector: 'Rejector',
  collector: 'Collector / bin',
  feeder: 'Infeed / feeder',
  trigger: 'Trigger',
  safety: 'Safety (not driven)',
  mechanical: 'Mechanical (not driven)',
  conveyor: 'Conveyor',
  encoder: 'Encoder',
  light: 'Signal light',
  camera: 'Camera',
  io: 'I/O module',
  plc: 'PLC',
};

/**
 * Shared with the designs page.
 *
 * A proposal is drawn by the same components as a running line on purpose: a
 * quote rendered by different code than the machine is a quote that stops
 * describing it.
 */
export { ROLE_ICON, ROLE_COLOUR, ROLE_LABEL };

export function LineView() {
  const isAdmin = useIsAdmin();
  const [devices, setDevices] = useState<LineDevice[]>([]);
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  // Customer, then station, then the line itself. A configuration describes one
  // physical machine, so it has to be clear WHICH machine before anything below
  // means anything.
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [stationId, setStationId] = useState<number | null>(null);
  const [stationLine, setStationLine] = useState<Record<string, number | string | null> | null>(null);
  const [readiness, setReadiness] = useState<Record<number, string[]>>({});
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [d, p, s] = await Promise.all([
      api.listLineDevices(stationId ?? undefined).catch(() => []),
      api.listDeviceProfiles().catch(() => []),
      api.listStations().catch(() => []),
    ]);
    setDevices(d);
    setProfiles(p);
    setStations(s);
    if (stationId) {
      const detail = await api.station(stationId).catch(() => null);
      setStationLine((detail?.line as Record<string, number | string | null>) ?? null);
    } else {
      setStationLine(null);
    }
    // Ask per device what still stands between it and being trusted to fire.
    const checks = await Promise.all(
      d.map((dev) => api.deviceReadiness(dev.id).catch(() => ({ ready: true, problems: [] }))),
    );
    setReadiness(Object.fromEntries(d.map((dev, i) => [dev.id, checks[i]!.problems])));
  }, [stationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Line</h1>
        <div className="panel muted">Configuring the line requires the ADMIN role.</div>
      </div>
    );
  }

  const remove = async (dev: LineDevice) => {
    if (!window.confirm(`Remove ${dev.name} from the line?`)) return;
    await api.deleteLineDevice(dev.id);
    await load();
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Line</h1>
        <button className="btn primary" onClick={() => setAdding((v) => !v)}>
          {adding ? '✕ Cancel' : '+ Add device'}
        </button>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <LinePicker
        stations={stations}
        customerId={customerId}
        stationId={stationId}
        onCustomer={(c) => {
          setCustomerId(c);
          setStationId(null);
        }}
        onStation={setStationId}
      />

      {adding && (
        <AddDeviceWizard
          profiles={profiles}
          stations={stations}
          onCancel={() => setAdding(false)}
          onAdded={async (name) => {
            setAdding(false);
            setNotice(`${name} added to the line.`);
            await load();
          }}
          onError={setError}
        />
      )}

      <div className="panel">
        <strong>Installed devices</strong>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          In the order a part meets them along the belt. Position is measured from the camera.
        </p>

        {devices.length === 0 ? (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
            Nothing configured yet. Add the camera trigger first, then whatever acts on the
            verdict.
          </p>
        ) : (
          <div className="grid" style={{ gap: 10, marginTop: 12 }}>
            {devices.map((d) => {
              const problems = readiness[d.id] ?? [];
              return (
                <div
                  key={d.id}
                  className="panel"
                  style={{
                    background: 'var(--panel-2)',
                    borderLeft: `4px solid ${
                      !d.enabled
                        ? 'var(--border)'
                        : problems.length
                          ? 'var(--warn)'
                          : 'var(--good)'
                    }`,
                    opacity: d.enabled ? 1 : 0.6,
                  }}
                >
                  <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                      {/* The role's colour lives here so the border stays free
                          to mean readiness. */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          fontSize: 18,
                          flexShrink: 0,
                          color: ROLE_COLOUR[d.role] ?? 'var(--text-dim)',
                          background: `color-mix(in srgb, ${ROLE_COLOUR[d.role] ?? '#64748b'} 16%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${ROLE_COLOUR[d.role] ?? '#64748b'} 36%, transparent)`,
                        }}
                      >
                        {ROLE_ICON[d.role] ?? '▪'}
                      </span>
                      <div>
                      <strong style={{ fontSize: 15 }}>
                        {d.name}
                      </strong>
                      <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                        {ROLE_LABEL[d.role] ?? d.role} · {d.driver}
                        {d.brand && ` · ${d.brand} ${d.model}`}
                        {d.positionMm !== null && ` · ${d.positionMm} mm from camera`}
                        {d.station && ` · ${d.station}`}
                      </div>
                      </div>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        className="btn sm"
                        onClick={async () => {
                          await api.updateLineDevice(d.id, { enabled: !d.enabled });
                          await load();
                        }}
                      >
                        {d.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button className="btn sm" onClick={() => void remove(d)}>
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="row" style={{ gap: 18, marginTop: 10, flexWrap: 'wrap', fontSize: 12 }}>
                    {d.actuationMs !== null && (
                      <span className="muted">
                        acts in <strong>{d.actuationMs} ms</strong>
                      </span>
                    )}
                    {d.dwellMs !== null && (
                      <span className="muted">
                        holds <strong>{d.dwellMs} ms</strong>
                      </span>
                    )}
                    {d.actsOn.length > 0 && (
                      <span className="muted">
                        acts on <strong>{d.actsOn.join(', ')}</strong>
                      </span>
                    )}
                    {Object.entries(d.settings)
                      .filter(([k]) => k !== 'required')
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <span key={k} className="muted">
                          {k}=<code>{String(v ?? '—')}</code>
                        </span>
                      ))}
                  </div>

                  {problems.length > 0 && (
                    // Not an error — the device is configured, just not yet
                    // trustworthy. Saying which part is missing is the whole
                    // value of the check.
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--warn)' }}>
                      ▲ Not ready to run:
                      <ul style={{ margin: '4px 0 0 18px' }}>
                        {problems.map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <LineSheet stationId={stationId} reloadKey={devices.length} />

      <Configurator
        devices={devices}
        stationId={stationId}
        saved={stationLine}
        onSaved={async () => {
          setNotice('Line description saved to this station.');
          await load();
        }}
        onError={setError}
      />

      {/* The catalogue moved to its own page: configuring a line is "which of
          these goes where", maintaining prices is a different job done by a
          different person. Only the shortcut stays here. */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <strong>Equipment catalogue</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
              {profiles.length} items · {profiles.filter((p) => !p.verified).length} on an unverified
              profile. Prices and article codes are maintained under Catalogue.
            </p>
          </div>
          <a className="btn sm" href="/catalogue">
            Open catalogue →
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Adding a device, as a sequence of questions.
 *
 * What it does, then which one, then where it sits and how fast it acts. In
 * that order because each answer narrows the next: the role decides which
 * profiles are worth showing, and the profile decides which settings even
 * exist.
 */
export function AddDeviceWizard({
  profiles,
  stations,
  designId,
  onAdded,
  onCancel,
  onError,
}: {
  profiles: DeviceProfile[];
  stations: Station[];
  /** Adding to a proposal rather than to a machine. */
  designId?: number;
  onAdded: (name: string) => void | Promise<void>;
  onCancel: () => void;
  onError: (m: string) => void;
}) {
  const [role, setRole] = useState<string | null>(null);
  const [profile, setProfile] = useState<DeviceProfile | null>(null);
  const [name, setName] = useState('');
  const [stationId, setStationId] = useState<number | null>(null);
  const [positionMm, setPositionMm] = useState('');
  const [actuationMs, setActuationMs] = useState('');
  const [dwellMs, setDwellMs] = useState('');
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const roles = [...new Set(profiles.map((p) => p.role))];
  const forRole = profiles.filter((p) => p.role === role);

  const pick = (p: DeviceProfile) => {
    setProfile(p);
    setName((cur) => cur || `${ROLE_LABEL[p.role] ?? p.role} 1`);
    setActuationMs(p.actuationMs === null ? '' : String(p.actuationMs));
    setDwellMs(p.dwellMs === null ? '' : String(p.dwellMs));
    const initial: Record<string, string> = {};
    for (const [k, v] of Object.entries(p.defaultSettings)) {
      if (k === 'required') continue;
      initial[k] = v === null || v === undefined ? '' : String(v);
    }
    setSettings(initial);
  };

  const save = async () => {
    if (!profile) return;
    setBusy(true);
    try {
      // Numbers arrive from text inputs; a register left as "8" would be
      // written as a string and silently fail to match on the wire.
      const typed: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(settings)) {
        if (v === '') continue;
        typed[k] = /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v === 'true' ? true : v === 'false' ? false : v;
      }
      await api.createLineDevice({
        profileId: profile.id,
        designId,
        stationId: designId ? null : stationId,
        name: name.trim(),
        role: profile.role,
        driver: profile.driver,
        positionMm: positionMm === '' ? null : Number(positionMm),
        actuationMs: actuationMs === '' ? null : Number(actuationMs),
        dwellMs: dwellMs === '' ? null : Number(dwellMs),
        settings: typed,
      });
      await onAdded(name.trim());
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 20, borderLeft: '4px solid var(--accent)' }}>
      <strong>Add a device</strong>

      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14 }}>
        1 · What does it do?
      </h3>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {roles.map((r) => (
          <button
            key={r}
            className={`chip ${role === r ? 'selected' : ''}`}
            style={
              role === r
                ? {
                    borderColor: ROLE_COLOUR[r],
                    background: `color-mix(in srgb, ${ROLE_COLOUR[r]} 18%, transparent)`,
                  }
                : undefined
            }
            onClick={() => {
              setRole(r);
              setProfile(null);
            }}
          >
            <span style={{ color: ROLE_COLOUR[r] ?? 'var(--text-dim)' }}>{ROLE_ICON[r] ?? '▪'}</span>{' '}
            {ROLE_LABEL[r] ?? r}
          </button>
        ))}
      </div>

      {role && (
        <>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 18 }}>
            2 · Which one?
          </h3>
          <div className="grid" style={{ gap: 8, marginTop: 8 }}>
            {forRole.map((p) => (
              <button
                key={p.id}
                className={`chip ${profile?.id === p.id ? 'selected' : ''}`}
                style={{ display: 'block', textAlign: 'left', width: '100%' }}
                onClick={() => pick(p)}
              >
                <div style={{ fontWeight: 700 }}>
                  {p.brand} — {p.model}{' '}
                  {p.verified ? (
                    <span className="badge good" style={{ fontSize: 10 }}>verified</span>
                  ) : (
                    <span className="badge unsure" style={{ fontSize: 10 }}>unverified</span>
                  )}
                </div>
                {p.description && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                    {p.description}
                  </div>
                )}
                {p.notes && (
                  <div style={{ fontSize: 11, marginTop: 3, color: 'var(--warn)' }}>⚠ {p.notes}</div>
                )}
              </button>
            ))}
            {forRole.length === 0 && (
              <span className="muted" style={{ fontSize: 13 }}>
                No profiles for this role yet — add one in the catalogue below first.
              </span>
            )}
          </div>
        </>
      )}

      {profile && (
        <>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 18 }}>
            3 · How is it connected?
          </h3>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {Object.keys(settings).map((k) => {
              const isRequired = profile.required.includes(k);
              return (
                <div key={k} style={{ flex: 1, minWidth: 140 }}>
                  <label>
                    {k}
                    {isRequired && <span style={{ color: 'var(--warn)' }}> *</span>}
                  </label>
                  <input
                    value={settings[k] ?? ''}
                    onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))}
                    style={{
                      width: '100%',
                      borderColor: isRequired && !settings[k] ? 'var(--warn)' : undefined,
                    }}
                  />
                </div>
              );
            })}
          </div>

          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginTop: 18 }}>
            4 · Where and how fast?
          </h3>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
            </div>
            <div style={{ flex: '0 0 180px', display: designId ? 'none' : undefined }}>
              <label>Station</label>
              <select
                value={stationId ?? ''}
                onChange={(e) => setStationId(e.target.value ? Number(e.target.value) : null)}
                style={{ width: '100%' }}
              >
                <option value="">— this machine —</option>
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: '0 0 170px' }}>
              <label>Position (mm from camera)</label>
              <input
                value={positionMm}
                onChange={(e) => setPositionMm(e.target.value)}
                placeholder="500"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <label>Acts in (ms)</label>
              <input
                value={actuationMs}
                onChange={(e) => setActuationMs(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: '0 0 140px' }}>
              <label>Holds (ms)</label>
              <input value={dwellMs} onChange={(e) => setDwellMs(e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            The timings came from the profile as a starting point. <strong>Measure them</strong> on
            the real device — reject timing is only as good as these two numbers, and a guess here
            means missing the part.
          </p>

          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void save()}>
              {busy ? 'Adding…' : 'Add to line'}
            </button>
            <button className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function LinePicker({
  stations,
  customerId,
  stationId,
  onCustomer,
  onStation,
}: {
  stations: Station[];
  customerId: number | null;
  stationId: number | null;
  onCustomer: (id: number | null) => void;
  onStation: (id: number | null) => void;
}) {
  const customers = [
    ...new Map(
      stations
        .filter((s) => s.customerId)
        .map((s) => [s.customerId!, s.customer ?? `Customer ${s.customerId}`]),
    ),
  ];
  const forCustomer = stations.filter((s) => !customerId || s.customerId === customerId);

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 240px' }}>
          <label>Customer</label>
          <select
            value={customerId ?? ''}
            onChange={(e) => onCustomer(e.target.value ? Number(e.target.value) : null)}
            style={{ width: '100%' }}
          >
            <option value="">— all —</option>
            {customers.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: '0 0 280px' }}>
          <label>Station</label>
          <select
            value={stationId ?? ''}
            onChange={(e) => onStation(e.target.value ? Number(e.target.value) : null)}
            style={{ width: '100%' }}
          >
            <option value="">— this machine —</option>
            {forCustomer.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.lineName ? ` · ${s.lineName}` : ''}
              </option>
            ))}
          </select>
        </div>
        <span className="muted" style={{ fontSize: 12, paddingBottom: 8 }}>
          {stationId
            ? 'Everything below describes this station. Its configuration is stored with it.'
            : 'Describing the line on this machine.'}
        </span>
      </div>
    </div>
  );
}

/**
 * Line configurator.
 *
 * Enter the belt's dimensions and speed and it works out where equipment has to
 * sit. Every number is arithmetic on a moving belt — distance is speed times
 * time — so there is nothing to tune and nothing to disagree with.
 *
 * The inspection time is the input people underestimate. A verdict is not
 * instant, and the part keeps moving while it is computed: at 300 mm/s a
 * half-second inspection means the part has travelled 150 mm before anything
 * can act on it. A rejector closer than that fires at empty belt every time,
 * with nothing in the logs to explain why.
 */
export function Configurator({
  devices,
  stationId,
  saved,
  onSaved,
  onError,
}: {
  devices: LineDevice[];
  stationId: number | null;
  saved: Record<string, number | string | null> | null;
  onSaved: () => void | Promise<void>;
  onError: (m: string) => void;
}) {
  const [speed, setSpeed] = useState('300');
  const [partLength, setPartLength] = useState('80');
  const [gap, setGap] = useState('120');
  const [inspection, setInspection] = useState('520');
  const [worst, setWorst] = useState('900');
  const [mmPerPulse, setMmPerPulse] = useState('');
  const [busy, setBusy] = useState(false);

  // Adopt whatever this station already says about itself. Re-typing these from
  // memory each visit is how a line ends up analysed against numbers nobody
  // checked.
  useEffect(() => {
    if (!saved) return;
    const v = (k: string, fallback: string) =>
      saved[k] === null || saved[k] === undefined ? fallback : String(saved[k]);
    setSpeed(v('beltSpeedMmS', '300'));
    setPartLength(v('partLengthMm', '80'));
    setGap(v('partGapMm', '120'));
    setInspection(v('inspectionMs', '520'));
    setWorst(v('inspectionWorstMs', '900'));
    setMmPerPulse(v('mmPerPulse', ''));
  }, [saved]);

  const input = {
    speedMmS: Number(speed) || 1,
    partLengthMm: Number(partLength) || 1,
    gapMm: Number(gap) || 0,
    inspectionMs: Number(inspection) || 1,
    inspectionWorstMs: Number(worst) || Number(inspection) || 1,
    mmPerPulse: mmPerPulse ? Number(mmPerPulse) : undefined,
  };

  const analysis = analyseLine(
    input,
    devices
      .filter((d) => d.enabled)
      .map((d) => ({
        name: d.name,
        role: d.role,
        positionMm: d.positionMm,
        actuationMs: d.actuationMs,
        dwellMs: d.dwellMs,
      })),
  );
  const advice = suggestRejector(input);

  const num = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <div style={{ flex: '0 0 150px' }}>
      <label>{label}</label>
      <input value={value} onChange={(e) => set(e.target.value)} style={{ width: '100%' }} />
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <strong>Configurator</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        What the line can do, and where equipment has to sit. Pure arithmetic on a moving belt —
        change a number and everything below follows.
      </p>

      <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        {num('Belt speed', speed, setSpeed, 'mm/s')}
        {num('Part length', partLength, setPartLength, 'mm, along the belt')}
        {num('Gap', gap, setGap, 'mm between parts')}
        {num('Inspection', inspection, setInspection, 'ms, typical')}
        {num('Worst case', worst, setWorst, 'ms, slowest')}
        {num('Encoder', mmPerPulse, setMmPerPulse, 'mm per pulse')}
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        {/* Not a hypothetical: these are the numbers this project measured on
            the Pi, and they decide the whole layout. */}
        Use MEASURED inspection times. A Raspberry Pi 5 running PatchCore does about 520 ms typical
        and 900 ms worst; a desktop GPU is under 50 ms. The difference changes where the rejector
        must go by half a metre.
      </p>

      <div
        className="row"
        style={{ gap: 28, flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}
      >
        <Figure label="Throughput" value={`${analysis.throughputPerMin.toFixed(0)}/min`} />
        <Figure label="Time per part" value={`${analysis.partIntervalMs.toFixed(0)} ms`} />
        <Figure
          label="Travel while judging"
          value={`${analysis.travelDuringInspectionMm.toFixed(0)} mm`}
          hint="part moves this far before a verdict exists"
        />
        <Figure
          label="Rejector no closer than"
          value={`${analysis.minRejectDistanceWorstMm.toFixed(0)} mm`}
          hint={`${analysis.minRejectDistanceMm.toFixed(0)} mm at typical speed`}
        />
      </div>

      {(analysis.findings.length > 0 || analysis.placements.some((p) => p.findings.length > 0)) && (
        <div style={{ marginTop: 16 }}>
          {analysis.findings.map((f, i) => (
            <FindingRow key={`l${i}`} finding={f} />
          ))}
          {analysis.placements.map((p) =>
            p.findings.map((f, i) => <FindingRow key={`${p.name}${i}`} finding={f} where={p.name} />),
          )}
        </div>
      )}

      {stationId && (
        <div className="row" style={{ marginTop: 16, gap: 12, alignItems: 'center' }}>
          <button
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.updateStation(stationId, {
                  beltSpeedMmS: Number(speed) || null,
                  partLengthMm: Number(partLength) || null,
                  partGapMm: Number(gap) || null,
                  inspectionMs: Number(inspection) || null,
                  inspectionWorstMs: Number(worst) || null,
                  mmPerPulse: mmPerPulse ? Number(mmPerPulse) : null,
                });
                await onSaved();
              } catch (e) {
                onError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Saving…' : 'Save to this station'}
          </button>
          {saved?.updatedAt && (
            <span className="muted" style={{ fontSize: 12 }}>
              last described {new Date(String(saved.updatedAt)).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <strong style={{ fontSize: 13 }}>Which actuator suits this line</strong>
        {advice.map((f, i) => (
          <FindingRow key={`a${i}`} finding={f} />
        ))}
      </div>
    </div>
  );
}

export function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && (
        <div className="muted" style={{ fontSize: 11 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function FindingRow({ finding, where }: { finding: Finding; where?: string }) {
  // Icon and word, never colour alone — this is read on a shop floor.
  const style: Record<string, { colour: string; icon: string }> = {
    error: { colour: 'var(--bad)', icon: '■' },
    warning: { colour: 'var(--warn)', icon: '▲' },
    info: { colour: 'var(--text-dim)', icon: '·' },
  };
  const s = style[finding.severity]!;
  return (
    <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
      <span style={{ color: s.colour, fontWeight: 700 }}>{s.icon}</span>{' '}
      {where && <strong>{where}: </strong>}
      <span style={{ color: finding.severity === 'info' ? 'var(--text-dim)' : undefined }}>
        {finding.message}
      </span>
      {finding.fix && (
        <div className="muted" style={{ marginLeft: 18, fontSize: 12 }}>
          → {finding.fix}
        </div>
      )}
    </div>
  );
}


/**
 * The line as a sheet somebody can print and take to the machine.
 *
 * Grouped by role and ordered along the belt, because that is how the line is
 * walked. The three counts at the top are the ones that decide whether this
 * sheet can be trusted: equipment whose address was never proven, devices
 * missing a required setting, and anything switched off. A line sheet that
 * lists hardware without saying which of it is unverified gets believed for
 * the wrong reasons.
 */
export function LineSheet({
  stationId,
  designId,
  reloadKey,
}: {
  stationId?: number | null;
  designId?: number | null;
  reloadKey: number;
}) {
  const [data, setData] = useState<{
    items: Array<Record<string, unknown>>;
    counts: Record<string, number>;
    total: number;
    unverified: number;
    incomplete: number;
    disabled: number;
  } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    void api
      .lineSummary(stationId ?? undefined, designId ?? undefined)
      .then(setData)
      .catch(() => setData(null));
  }, [stationId, designId, reloadKey]);

  if (!data || data.total === 0) return null;

  const cell: React.CSSProperties = { fontSize: 12, padding: '6px 8px' };

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>Line sheet</strong>
        <button className="btn sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : `Show all ${data.total}`}
        </button>
      </div>

      <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        {Object.entries(data.counts)
          .sort((a, b) => b[1] - a[1])
          .map(([role, n]) => (
            <span
              key={role}
              className="chip"
              style={{
                borderColor: `color-mix(in srgb, ${ROLE_COLOUR[role] ?? '#64748b'} 40%, transparent)`,
              }}
            >
              <span style={{ color: ROLE_COLOUR[role] ?? 'var(--text-dim)' }}>
                {ROLE_ICON[role] ?? '▪'}
              </span>{' '}
              {n}× {ROLE_LABEL[role] ?? role}
            </span>
          ))}
      </div>

      {(data.unverified > 0 || data.incomplete > 0 || data.disabled > 0) && (
        <div className="row" style={{ gap: 18, marginTop: 12, fontSize: 12, flexWrap: 'wrap' }}>
          {data.incomplete > 0 && (
            <span style={{ color: 'var(--bad)' }}>
              ■ {data.incomplete} missing a required setting
            </span>
          )}
          {data.unverified > 0 && (
            <span style={{ color: 'var(--warn)' }}>
              ▲ {data.unverified} on an unverified profile
            </span>
          )}
          {data.disabled > 0 && <span className="muted">· {data.disabled} switched off</span>}
        </div>
      )}

      {open && (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th style={cell}>Pos.</th>
                <th style={cell}>Device</th>
                <th style={cell}>Equipment</th>
                <th style={cell}>Address</th>
                <th style={cell}>Timing</th>
                <th style={cell}>Acts on</th>
                <th style={cell}>State</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => {
                const role = String(i.role);
                const missing = (i.missing as string[]) ?? [];
                return (
                  <tr key={String(i.id)} style={{ opacity: i.enabled ? 1 : 0.5 }}>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }} className="muted">
                      {i.positionMm === null ? '—' : `${i.positionMm} mm`}
                    </td>
                    <td style={cell}>
                      <span style={{ color: ROLE_COLOUR[role] ?? 'var(--text-dim)' }}>
                        {ROLE_ICON[role] ?? '▪'}
                      </span>{' '}
                      <strong>{String(i.name)}</strong>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {ROLE_LABEL[role] ?? role}
                        {i.collector ? ` → ${String(i.collector)}` : ''}
                      </div>
                    </td>
                    <td style={cell} className="muted">
                      {i.brand ? `${i.brand} ${i.model}` : '—'}
                      <div style={{ fontSize: 11 }}>{String(i.driver)}</div>
                    </td>
                    <td style={cell} className="muted">
                      {i.address ? <code style={{ fontSize: 11 }}>{String(i.address)}</code> : '—'}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }} className="muted">
                      {i.actuationMs === null && i.dwellMs === null
                        ? '—'
                        : `${i.actuationMs ?? '?'} / ${i.dwellMs ?? '?'} ms`}
                    </td>
                    <td style={cell} className="muted">
                      {(i.actsOn as string[])?.length
                        ? (i.actsOn as string[]).join(', ')
                        : role === 'rejector'
                          ? 'all BAD'
                          : '—'}
                    </td>
                    <td style={cell}>
                      {missing.length > 0 ? (
                        <span style={{ color: 'var(--bad)', fontSize: 11 }}>
                          ■ needs {missing.join(', ')}
                        </span>
                      ) : i.verified === false ? (
                        <span style={{ color: 'var(--warn)', fontSize: 11 }}>▲ unverified</span>
                      ) : !i.enabled ? (
                        <span className="muted" style={{ fontSize: 11 }}>
                          off
                        </span>
                      ) : (
                        <span style={{ color: 'var(--good)', fontSize: 11 }}>● ready</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
